/**
 * THE REQUEST-METADATA ALLOWLIST and the sanitizer that applies it: which client-supplied request fields may be
 * persisted at all, and what counts as a present value. ACTIVITY+AUTH surface -- a key added here is PII exposure --
 * so the list is spelled once, beside the header and geo vocabulary it is built from (`const/request-metadata-headers.ts`).
 * Pure over the const floor: `readPayload` (`const/json-parse.ts`) is the parse core, `isPresentValue` the presence test.
 * Lifted out of `utils/activity/request-metadata.ts` 2026-09-02 at that leaf's UTILS_MAX_LINES utils cap
 * (`QUAL-FILE-SIZE-CAP-HEADROOM-TIGHT-001`); the collector and merger there compose these.
 */

import { isPresentValue } from '@federa/const/invalid-string-tokens';
import { readPayload } from '@federa/const/json-parse';
import { isPlainObject, nullIfEmpty } from '@federa/const/record-guards';
import { GEO_KEYS, REQUEST_METADATA_HEADERS } from '@federa/const/request-metadata-headers';
import type { RequestMetadata } from '@federa/types/request-metadata';

/**
 * The three NON-GEO members of the allowlist, as one namespace rather than three flat exports
 * (`QUAL-CONST-GROUP-001`). Each is a `RequestMetadata` key spelled as data because the sanitizer reads keys off a
 * client-supplied record, where a key is a VALUE and not a type position; the geo members come from `GEO_KEYS` and
 * are never restated here.
 */
export const REQUEST_METADATA_KEY = {
	USER_AGENT: 'userAgent',
	CLIENT_IP: 'clientIp',
	BOT_SCORE: 'botScore',
} as const;

/** @runtime-derived-ssot-helper (const-leaf-pinned): Assign value to the metadata draft when valid (not null, undefined, empty, or the strings 'null'/'undefined'); values are raw unknown header reads. */
export function assignIfPresent(target: Partial<Record<keyof RequestMetadata, unknown>>, key: keyof RequestMetadata, value: unknown): void {
	if (isPresentValue(value)) {
		target[key] = value;
	}
}

/** The keys a persisted request-metadata record may carry -- a data-minimization set, named for what it admits to
 *  storage. The geo members come from the `GEO_KEYS` SSOT the collector also reads, so the two lists cannot drift; the
 *  four non-geo members are this sanitizer's own surface. Order is preserved exactly. */
export const REQUEST_METADATA_PERSISTED_KEYS = Object.freeze([
	REQUEST_METADATA_KEY.CLIENT_IP,
	...GEO_KEYS,
	REQUEST_METADATA_KEY.USER_AGENT,
	REQUEST_METADATA_KEY.BOT_SCORE,
	REQUEST_METADATA_HEADERS.ORIGIN,
] as const) satisfies readonly (keyof RequestMetadata)[];

/**
 * Sanitize request metadata by filtering to allowed keys only. Handles string input (JSON parsing) and object input.
 * @runtime-derived-ssot-helper (const-leaf-pinned): Sanitize request metadata by filtering to allowed keys only.
 */
export function sanitizeRequestMetadata(candidate: string | object | null | undefined): RequestMetadata | null {
	if (candidate === null || candidate === undefined) return null;

	const source = readPayload(candidate);

	if (!isPlainObject(source)) return null;

	const output: Record<string, unknown> = {};
	for (const key of REQUEST_METADATA_PERSISTED_KEYS) {
		assignIfPresent(output, key, source[key]);
	}

	return nullIfEmpty(output);
}
